import type { Table } from '@tanstack/react-table';
import { Calculator, FilterX, Package, ShoppingCart } from 'lucide-react';
import type { ReactNode } from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import type { DiscountStatusType } from './DiscountStatus';
import DiscountStatusFilter from './DiscountStatus';
import LastUpdateFilter from './LastUpade';
import type { StatusType } from './RetailStatus';
import RetailStatusFilter from './RetailStatus';
import type { VariousFilterType } from './VariousFilter';
import VariousFilterFilter from './VariousFilter';
import WholeSaleStatusFilter from './WholeSaleStatus';

interface TableFilterProps {
  table: Table<IOneItemInPrepareForRequest>;
  children?: ReactNode;
}

function TableFilter({ table, children }: TableFilterProps) {
  // Get items directly from table data to ensure we track changes
  const items = table.options.data;
  const [discountType, setDiscountType] =
    useState<DiscountStatusType>(undefined);
  const [retailType, setRetailType] = useState<StatusType>(undefined);
  const [wholeSaleType, setWholeSaleType] = useState<StatusType>(undefined);
  const [variousFilterType, setVariousFilterType] =
    useState<VariousFilterType>(undefined);
  const [isLastUpdate, setIsLastUpdate] = useState<'true' | 'false'>('true');
  const [searchValue, setSearchValue] = useState<string>('');

  // Debounce the filter update: only apply the search after 300ms of inactivity
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      table.getColumn('ClientItemName')?.setFilterValue(searchValue);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchValue, table]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value);
    },
    [],
  );

  const isFilterActive = useMemo(
    () =>
      !!discountType ||
      !!retailType ||
      !!wholeSaleType ||
      !!variousFilterType ||
      isLastUpdate === 'false',
    [discountType, retailType, wholeSaleType, variousFilterType, isLastUpdate],
  );

  const handleResetFilters = useCallback(() => {
    setDiscountType(undefined);
    table.getColumn('DiscountStatus')?.setFilterValue(undefined);
    setRetailType(undefined);
    table.getColumn('RetailStatus')?.setFilterValue(undefined);
    setWholeSaleType(undefined);
    table.getColumn('WholeSaleSalesStatus')?.setFilterValue(undefined);
    setVariousFilterType(undefined);
    table.getColumn('Price')?.setFilterValue(undefined);
    setIsLastUpdate('true');
    table.getColumn('isLastUpdate')?.setFilterValue('true');
    setSearchValue('');
    table.getColumn('ClientItemName')?.setFilterValue('');
  }, [table]);

  // Remove the hacky interval-based force update as proper dependencies will handle updates

  // Calculate summary data for the order - more optimized with better memoization
  const summaryData = useMemo(() => {
    // Get the current filtered rows and their data
    const filteredRows = table.getFilteredRowModel().rows;

    // Use a single pass through the data to calculate all metrics at once
    const { itemsCount, totalOrder, retailTotal } = filteredRows.reduce(
      (acc, row) => {
        const item = row.original;
        const qty = item.Qty || 0;

        // Skip calculations for items with no quantity
        if (qty <= 0) return acc;

        // Count items with quantity
        acc.itemsCount += 1;

        // Calculate price values
        const price = item.Price || 0;
        const discount = item.DiscountEdit || 0;
        const discountMultiplier = (100 - discount) / 100;

        // Add to totals
        acc.retailTotal += price * qty;
        acc.totalOrder += price * qty * discountMultiplier;

        return acc;
      },
      { itemsCount: 0, totalOrder: 0, retailTotal: 0 },
    );

    return {
      itemsCount,
      totalOrder: Math.round(totalOrder * 100) / 100,
      retailTotal: Math.round(retailTotal * 100) / 100,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    table,
    discountType,
    retailType,
    wholeSaleType,
    variousFilterType,
    isLastUpdate,
    searchValue,
    items,
  ]);

  return (
    <div className="w-full">
      <div className="grid grid-cols-[250px_1fr] gap-3">
        {/* Filter Panel - Wider width */}
        <div className="h-[85vh] overflow-y-auto rounded-md border bg-card p-2 shadow-sm">
          {/* Order Summary Card */}
          <Card className="my-2 border-primary/20 bg-primary/5 p-3 shadow-sm">
            <h3 className="border-b border-primary/20 pb-1 text-center text-xs font-semibold text-primary">
              ملخص الطلبية
            </h3>
            <div className="mt-2 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Package className="h-4 w-4 text-primary/70" />
                  <span className="text-xs font-medium text-primary/80">
                    عدد الأصناف:
                  </span>
                </div>
                <span
                  className={cn(
                    'text-sm font-semibold text-primary',
                    summaryData.itemsCount > 0
                      ? 'text-green-600 dark:text-green-400'
                      : '',
                  )}
                >
                  {summaryData.itemsCount}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <ShoppingCart className="h-4 w-4 text-primary/70" />
                  <span className="text-xs font-medium text-primary/80">
                    إجمالي الطلبية:
                  </span>
                </div>
                <span
                  className={cn(
                    'text-sm font-semibold',
                    summaryData.totalOrder > 0
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-primary',
                  )}
                >
                  {summaryData.totalOrder} ج.م
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <Calculator className="h-4 w-4 text-primary/70" />
                  <span className="text-xs font-medium text-primary/80">
                    إجمالي الجمهور:
                  </span>
                </div>
                <span
                  className={cn(
                    'text-sm font-semibold',
                    summaryData.retailTotal > 0
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-primary',
                  )}
                >
                  {summaryData.retailTotal} ج.م
                </span>
              </div>
            </div>
          </Card>
          <div className="space-y-2">
            <div className="mb-1">
              <h3 className="border-b pb-1 text-center text-xs font-semibold">
                خيارات التصفية
              </h3>
            </div>
            <Input
              value={searchValue}
              onChange={handleSearchChange}
              className="h-8 w-full text-sm"
              placeholder="بحث عن صنف"
              size={16}
            />
            <DiscountStatusFilter
              table={table}
              discountType={discountType}
              setDiscountType={setDiscountType}
            />
            <RetailStatusFilter
              table={table}
              statusType={retailType}
              setStatusType={setRetailType}
            />
            <WholeSaleStatusFilter
              table={table}
              wholeSaleType={wholeSaleType}
              setWholeSaleType={setWholeSaleType}
            />
            <VariousFilterFilter
              table={table}
              variousFilterType={variousFilterType}
              setVariousFilterType={setVariousFilterType}
            />
            <LastUpdateFilter
              isLastUpdate={isLastUpdate}
              setIsLastUpdate={setIsLastUpdate}
              table={table}
            />

            {isFilterActive && (
              <Button
                className="mt-2 h-7 w-full"
                size="sm"
                variant="destructive"
                onClick={handleResetFilters}
              >
                <FilterX className="mr-1 h-3.5 w-3.5" />
                <span className="text-xs">الغاء الفلاتر</span>
              </Button>
            )}
          </div>
        </div>

        {/* Table container - Flexible 1fr width */}
        <div className="overflow-hidden">{children}</div>
      </div>
    </div>
  );
}

export default memo(TableFilter);
